import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

const { 
  FiCheck, FiCalendar, FiShield, FiHeart, 
  FiArrowRight, FiMenu, FiX, FiMail, FiUser, FiHome, FiSend,
  FiCoffee, FiTrash2, FiFileText, FiDollarSign
} = FiIcons;

export default function LandingPage() {
  const navigate = useNavigate();
  const { signUp, user, loading: authLoading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  
  // Signup State
  const [formData, setFormData] = useState({ familyName: '', adminName: '', email: '', password: '' });
  const [signupStatus, setSignupStatus] = useState('idle'); // idle, submitting, success, error
  const [signupError, setSignupError] = useState('');

  const [stripeConfig, setStripeConfig] = useState(null);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
      fetchStripeConfig();
  }, []);

  const fetchStripeConfig = async () => {
      const { data } = await supabase.from('app_settings').select('value').eq('key', 'public_stripe_config').maybeSingle();
      if (data?.value) setStripeConfig(data.value);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupStatus('submitting');
    setSignupError('');
    
    try {
      if (formData.password.length < 6) {
          throw new Error('Passwort muss mindestens 6 Zeichen haben.');
      }

      const { data, error } = await signUp(formData.email, formData.password, formData.adminName, formData.familyName);
      
      if (error) {
          // Check if it's the specific duplicate email error (handled in AuthContext now)
          throw error;
      }
      
      setSignupStatus('success');
      // Redirect after short delay
      setTimeout(() => {
          navigate('/login');
      }, 2000);
      
    } catch (error) {
      console.error('Signup Error:', error);
      
      // Improve UI for existing user
      let msg = error.message || 'Fehler bei der Registrierung.';
      if (msg.includes('already registered') || msg.includes('bereits verwendet')) {
          msg = '⚠️ Diese E-Mail wird bereits verwendet. Bitte einloggen.';
      }
      
      setSignupError(msg);
      setSignupStatus('error');
    }
  };
  
  const scrollToSection = (id) => {
      const element = document.getElementById(id);
      if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setMobileMenuOpen(false);
      }
  };
  
  const features = [
    { icon: FiCalendar, title: "Intelligenter Kalender", desc: "Koordinieren Sie Termine mühelos. Synchronisation in Echtzeit (Google, Apple) für die ganze Familie." },
    { icon: FiCoffee, title: "Mahlzeiten & Einkauf", desc: "KI-gestützte Essensplanung und automatischer Einkaufszettel." },
    { icon: FiTrash2, title: "Müllabfuhr-Erinnerung", desc: "Vergessen Sie nie wieder die Tonne rauszustellen." },
    { icon: FiDollarSign, title: "Haushaltsbuch", desc: "Behalten Sie den Überblick über Ausgaben und Projekte." },
    { icon: FiFileText, title: "Dokumente & Verträge", desc: "Speichern Sie wichtige Unterlagen sicher ab. Erinnerungen an Fristen." },
    { icon: FiHeart, title: "Party & Erinnerungen", desc: "Planen Sie Feste und teilen Sie Fotos im privaten Album." },
    { icon: FiShield, title: "Notfall & Sicherheit", desc: "Alle wichtigen Kontakte griffbereit, wenn es darauf ankommt." }
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-blue-100">
      <nav className="fixed w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
                <span className="text-white font-bold text-xl">F</span>
              </div>
              <span className="font-bold text-xl tracking-tight">FamilyManager</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('features')} className="text-gray-600 hover:text-blue-600 font-medium">Funktionen</button>
              <button onClick={() => scrollToSection('pricing')} className="text-gray-600 hover:text-blue-600 font-medium">Preise</button>
              <Link to="/login" className="text-gray-900 font-bold hover:text-blue-600">Login</Link>
              <button onClick={() => setShowSignupModal(true)} className="px-5 py-2.5 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-all hover:shadow-lg">
                1 Monat Gratis testen
              </button>
            </div>
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 p-4 space-y-4 shadow-xl">
             <button onClick={() => scrollToSection('features')} className="block text-gray-600 font-medium">Funktionen</button>
             <button onClick={() => scrollToSection('pricing')} className="block text-gray-600 font-medium">Preise</button>
             <Link to="/login" className="block text-blue-600 font-bold">Login</Link>
          </div>
        )}
      </nav>

      <section className="pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden relative">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-50"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 bg-purple-100 rounded-full blur-3xl opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block py-1 px-3 rounded-full bg-blue-50 text-blue-600 text-sm font-bold mb-6 border border-blue-100">
              Inklusive 1 Monat Gratis-Testzeit 🚀
            </span>
            <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tight text-gray-900 mb-8 leading-tight">
              Organisieren Sie Ihr <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Familienleben neu.</span>
            </h1>
            <p className="max-w-2xl mx-auto text-xl text-gray-500 mb-10 leading-relaxed">
              FamilyManager ist das Betriebssystem für moderne Familien. Vom Haushalt bis zur Partyplanung – alles an einem Ort.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => setShowSignupModal(true)} className="w-full sm:w-auto px-8 py-4 bg-blue-600 text-white rounded-full font-bold text-lg hover:bg-blue-700 transition-all shadow-xl shadow-blue-200">
                Jetzt kostenlos starten
              </button>
              <button onClick={() => scrollToSection('features')} className="w-full sm:w-auto px-8 py-4 bg-white text-gray-700 border border-gray-200 rounded-full font-bold text-lg hover:bg-gray-50 transition-all">
                Funktionen
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Das Betriebssystem für Ihre Familie</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Endlich Schluss mit Zettelwirtschaft und WhatsApp-Chaos.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600">
                  <SafeIcon icon={feature.icon} className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-500 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
           <h2 className="text-3xl font-bold text-gray-900 mb-4">Einfach und Transparent</h2>
           <p className="text-gray-500 mb-12">Starten Sie mit 1 Monat gratis. Danach wählen Sie Ihren Plan.</p>
           
           <div className="flex flex-col md:flex-row justify-center gap-6">
              {/* Yearly Plan */}
              <div className="p-8 bg-gray-50 rounded-2xl border border-gray-100 max-w-sm w-full mx-auto hover:border-blue-200 transition-colors">
                 <h3 className="text-xl font-bold mb-2">Jahresabo</h3>
                 <p className="text-4xl font-extrabold text-blue-600 mb-4">39€ <span className="text-sm text-gray-500 font-normal">/ Jahr</span></p>
                 <p className="text-gray-500 mb-6 text-sm">Entspricht 3,25€ pro Monat</p>
                 <ul className="text-left space-y-3 mb-8 text-sm text-gray-600">
                    <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-green-500 mr-2" /> Alle Features</li>
                    <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-green-500 mr-2" /> Unbegrenzte Mitglieder</li>
                    <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-green-500 mr-2" /> 1 Monat Gratis vorab</li>
                 </ul>
                 <button onClick={() => setShowSignupModal(true)} className="w-full py-3 bg-white border-2 border-blue-100 rounded-xl text-blue-600 font-bold hover:bg-blue-50 hover:border-blue-200 transition-all">
                   Jetzt starten
                 </button>
              </div>

              {/* Biannual Plan (Bestseller) */}
              <div className="p-8 bg-gray-900 text-white rounded-2xl shadow-xl max-w-sm w-full mx-auto transform scale-105 border-2 border-purple-500 relative">
                 <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-xl rounded-tr-xl">BESTER WERT</div>
                 <h3 className="text-xl font-bold mb-2">2-Jahres-Paket</h3>
                 <p className="text-4xl font-extrabold text-white mb-4">64€ <span className="text-sm text-gray-400 font-normal">/ 2 Jahre</span></p>
                 <p className="text-purple-200 mb-6 text-sm">Nur 2,66€ pro Monat – Sie sparen 18%</p>
                 <ul className="text-left space-y-3 mb-8 text-sm text-gray-300">
                    <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-purple-400 mr-2" /> Dauerhaft günstiger</li>
                    <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-purple-400 mr-2" /> Preisgarantie</li>
                    <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-purple-400 mr-2" /> 1 Monat Gratis vorab</li>
                 </ul>
                 <button onClick={() => setShowSignupModal(true)} className="w-full py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-all shadow-lg shadow-purple-900/50">
                   Jetzt sichern
                 </button>
              </div>
           </div>
        </div>
      </section>

      <footer className="bg-gray-50 border-t border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center gap-2 mb-4 md:mb-0">
            <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center text-white font-bold">F</div>
            <span className="font-bold text-gray-900">FamilyManager</span>
          </div>
          <div className="text-sm text-gray-500">&copy; {new Date().getFullYear()} FamilyManager.</div>
        </div>
      </footer>

      {/* Signup Modal */}
      <AnimatePresence>
        {showSignupModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative overflow-hidden">
              <button onClick={() => setShowSignupModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} className="w-6 h-6" /></button>
              
              {signupStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><SafeIcon icon={FiCheck} className="w-10 h-10 text-green-600" /></div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Willkommen!</h3>
                  <p className="text-gray-600 mb-8">Ihr Account wurde erstellt und der <strong>Gratis-Monat</strong> aktiviert.</p>
                  <p className="text-sm text-gray-400">Sie werden weitergeleitet...</p>
                </div>
              ) : (
                <form onSubmit={handleSignup} className="space-y-4">
                   <h3 className="text-2xl font-bold text-gray-900">Account erstellen</h3>
                   <p className="text-gray-500 text-sm">Starten Sie jetzt Ihren kostenlosen Probemonat.</p>
                   
                   {signupError && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{signupError}</div>}
                   
                   <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Familienname</label>
                      <input type="text" required value={formData.familyName} onChange={e => setFormData({...formData, familyName: e.target.value})} className="w-full p-3 border rounded-xl" placeholder="Familie Muster" />
                   </div>
                   <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Ihr Name</label>
                      <input type="text" required value={formData.adminName} onChange={e => setFormData({...formData, adminName: e.target.value})} className="w-full p-3 border rounded-xl" placeholder="Max Muster" />
                   </div>
                   <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">E-Mail</label>
                      <input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full p-3 border rounded-xl" placeholder="max@muster.de" />
                   </div>
                   <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Passwort</label>
                      <input type="password" required value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="w-full p-3 border rounded-xl" placeholder="******" minLength={6} />
                   </div>
                   
                   <button type="submit" disabled={signupStatus === 'submitting'} className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 mt-2">
                      {signupStatus === 'submitting' ? 'Wird erstellt...' : 'Gratis Account erstellen'}
                   </button>
                   
                   <div className="text-center text-sm text-gray-400 mt-4">
                     Bereits einen Account? <Link to="/login" className="text-blue-600 font-bold hover:underline">Login</Link>
                   </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}